/**
 * Main Application Entry Point
 *
 * Demonstrates integration of:
 * - Bootstrap 5.3 for UI components
 * - SQLocal (SQLite in browser via OPFS)
 * - BroadcastChannel for cross-tab sync
 * - OpenLayers map with ol-ext LayerSwitcher
 * - PWA features (Service Worker, install prompt, offline detection)
 * - Multi-language support (i18n)
 */

// Bootstrap CSS and JS
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
// Modal is no longer needed — all modals use the frosted-glass system.
import { Offcanvas } from 'bootstrap';

// Database module (uses SQLocal directly, BroadcastChannel for tab sync)
import {
  sql,
  dbReady,
  initSchema,
  addLocation,
  getLocations,
  getLocationCount,
  deleteLocation,
  getDatabaseStatus,
  downloadDatabase,
  onDatabaseChange,
  exportToGeoJSON,
  saveGpsTrailMeta,
  getSetting,
  setSetting,
  addLocationFix,
  getUserType,
  setUserType,
  saveLocationAttributes,
  getLocationAttributes,
  addLocationMedia,
  getLocationMedia,
  updateLocation,
  getGpsTrails
} from './src/database.js';

// Map component with OpenLayers and ol-ext LayerSwitcher
import { MapView } from './src/components/MapView.js';

// Measurement tools (Circle, Line)
import { MapTools } from './src/components/MapTools.js';

// PWA module (registers Service Worker, handles install/offline)
import { initPWA, isOnline, onOfflineChange } from './src/pwa.js';

// i18n module
import { t, setLanguage, getLanguage, initI18n, onLanguageChange, getCategoryLabel } from './src/i18n.js';

// Mesh layer (Bitchat-compatible, offline-capable chat between nearby users)
import { getMesh } from './src/mesh/MeshClient.js';
import { initNearbyPanel } from './src/components/NearbyPanel.js';

// LayerPicker — Design B; retained for restorePersisted() (saved basemap)
import { LayerPicker } from './src/components/LayerPicker.js';

// RadialDial — Design D basemap switcher (corner dial with sweep gesture)
import { RadialDial } from './src/components/RadialDial.js';

// RadialMenu — Option B "Radial Selection Wheel" (Show existing / Create new)
import { RadialMenu } from './src/components/RadialMenu.js';

// Weather alerts — Ghana Met Agency CAP feed via our cached proxy
import { fetchAlerts, getCachedAlerts, getAlertCount } from './src/alerts.js';

// Credits — open-source attributions (licence obligation, see src/credits.js)
import { openCreditsModal } from './src/components/CreditsModal.js';

// Amenity panel — purpose cards over the 128 OSM amenity kinds
import { openAmenityPanel, buildCategories } from './src/components/AmenityPanel.js';

// Start screen — Option D (half-wheel over the live map)
import { openStartScreen, startScreenDisabled, setStartScreenDisabled }
  from './src/components/StartScreen.js';

// Authentication (email OTP) — glass login dialog + session client
import { checkSession, logout, getCurrentEmail, getCurrentUser, getDisplayName, isSignedIn } from './src/auth.js';
import { openLoginDialog } from './src/components/LoginDialog.js';

// GPS tracking — reusable GeoTracker engine + BisaWofa (local-only) adapter
import { geoTracker } from './src/geotracker-bisawofa.js';
import { formatCoord, formatAccuracy, formatDistance, accuracyQuality } from './src/geotracker/geo-utils.js';
import { openTrailMetaForm } from './src/components/TrailMetaForm.js';

// Voice input (speech-to-text) — native Capacitor plugin or web Speech API
import { initVoiceInput } from './src/voice.js';

// Ambient presence tracking (vicinity alerts / recommendations) + consent
import { PresenceTracker } from './src/presence/PresenceTracker.js';
import { syncLocationFixes } from './src/sync/locationSync.js';
import { openGlassDialog, injectGlassStyles } from './src/components/glassForm.js';

// User types (role-based Add-Location fields) + on-device media store
import { getExtraLocationFields, getUserTypeDef, getUserTypeOptions } from './src/userTypes.js';
import { inspectMedia, saveMediaFile, getMediaObjectURL } from './src/media/mediaStore.js';

// Map instance (global for access across functions)
let mapView = null;
let mapTools = null;

// Current interaction mode: 'addLocation' | 'measureCircle' | 'measureLine'
let currentMode = 'addLocation';

// ============================================================================
// Draggable Bottom Sheet Controller
// ============================================================================

const bottomSheet = {
  el: null,
  backdrop: null,
  handle: null,
  closeBtn: null,
  body: null,
  state: 'hidden',   // 'hidden' | 'peek' | 'half' | 'full'

  // Snap points as translateY percentage (peek is computed at init).
  // 'hidden' is the default at rest now that the sheet no longer hosts
  // permanent content — it appears only when LayerPicker / Nearest POI /
  // future flows invoke open(). 'peek' is preserved for compatibility
  // with the drag-snap logic but is no longer the resting state.
  snaps: { hidden: 100, peek: 95, half: 50, full: 0 },

  // Touch / mouse tracking
  startY: 0,
  startTranslate: 0,
  currentTranslate: 100,
  isDragging: false,
  startTime: 0,

  init() {
    this.el = document.getElementById('bottom-sheet');
    this.backdrop = document.getElementById('bottom-sheet-backdrop');
    this.handle = this.el?.querySelector('.bottom-sheet-handle');
    this.closeBtn = this.el?.querySelector('.bottom-sheet-close');
    this.body = this.el?.querySelector('.bottom-sheet-body');

    if (!this.el || !this.handle) return;

    // Touch events on the handle
    this.handle.addEventListener('touchstart', (e) => this._onTouchStart(e), { passive: true });
    this.handle.addEventListener('touchmove', (e) => this._onTouchMove(e), { passive: false });
    this.handle.addEventListener('touchend', () => this._onDragEnd());

    // Mouse events on the handle (desktop)
    this.handle.addEventListener('mousedown', (e) => this._onMouseDown(e));

    // Close button + backdrop — use pointerup so iOS WKWebView fires
    // reliably (click events inside transformed parents can be flaky on
    // real iOS, even though they work in macOS Safari Responsive Design).
    // 'click' is kept as a belt-and-braces fallback for non-pointer devices.
    const closeOnTap = (e) => {
      e?.preventDefault?.();
      this.close();
    };
    ['pointerup', 'click'].forEach((evt) => {
      this.closeBtn?.addEventListener(evt, closeOnTap);
      this.backdrop?.addEventListener(evt, closeOnTap);
    });

    // Escape key — desktop / external-keyboard fallback
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.state !== 'hidden' && this.state !== 'peek') {
        this.close();
      }
    });

    // At rest the sheet is fully off-screen. _computePeek still runs so the
    // drag-snap logic has a valid peek value if the user drags the handle
    // (which only becomes accessible after the sheet has been opened by
    // a dock button / programmatic trigger).
    requestAnimationFrame(() => {
      this._computePeek();
      this.state = 'hidden';
      this.currentTranslate = 100;
      this.el.style.transform = `translateY(100%)`;
      this.el.classList.remove('state-open');
      this.el.setAttribute('aria-hidden', 'true');
    });
  },

  /** Open to half-screen (or full) */
  open(snapTo = 'half') {
    this.el.setAttribute('aria-hidden', 'false');
    this._setState(snapTo);
  },

  /** Dismiss the sheet entirely (slide off-screen). */
  close() {
    this._setState('hidden');
  },

  /** Toggle between hidden ↔ half. */
  toggle() {
    if (this.state === 'hidden' || this.state === 'peek') this.open('half');
    else this.close();
  },

  /**
   * Compute peek translateY so the handle sits just above the dock.
   * Sheet: bottom:0, height = vh−56.  Dock: 68 px from viewport bottom.
   * We want 28 px of handle visible above the dock, rest behind it.
   */
  _computePeek() {
    const h = this._getSheetHeight();
    if (h > 0) {
      const handleHeight = 28;
      const dockHeight = 68;
      this.snaps.peek = ((h - handleHeight - dockHeight) / h) * 100;
    }
  },

  _setState(state) {
    this.state = state;
    this.currentTranslate = this.snaps[state];
    this.el.classList.remove('no-transition');
    this.el.style.transform = `translateY(${this.currentTranslate}%)`;

    const isResting = state === 'hidden' || state === 'peek';
    if (isResting) {
      this.backdrop?.classList.remove('visible');
      this.el.setAttribute('aria-hidden', state === 'hidden' ? 'true' : 'false');
      // After slide-down transition finishes, drop z-index behind dock.
      // iOS WKWebView occasionally drops the transitionend event, so we
      // also schedule a hard fallback that matches the CSS transition
      // duration (300 ms) plus a small slack.
      const drop = () => {
        if (this.state === 'hidden' || this.state === 'peek') {
          this.el.classList.remove('state-open');
        }
      };
      const onEnd = () => {
        this.el.removeEventListener('transitionend', onEnd);
        drop();
      };
      this.el.addEventListener('transitionend', onEnd);
      setTimeout(drop, 360);
    } else {
      // Immediately raise z-index above dock
      this.el.classList.add('state-open');
      this.el.setAttribute('aria-hidden', 'false');
      this.backdrop?.classList.add('visible');
    }
  },

  _getSheetHeight() {
    return this.el.getBoundingClientRect().height;
  },

  // --- Touch handling ---

  _onTouchStart(e) {
    this._computePeek();          // recalc in case viewport changed
    this.el.classList.add('state-open');   // above dock while dragging
    this.isDragging = true;
    this.startY = e.touches[0].clientY;
    this.startTranslate = this.currentTranslate;
    this.startTime = Date.now();
    this.el.classList.add('no-transition');
  },

  _onTouchMove(e) {
    if (!this.isDragging) return;
    e.preventDefault();

    const deltaY = e.touches[0].clientY - this.startY;
    const sheetHeight = this._getSheetHeight();
    const deltaPercent = (deltaY / sheetHeight) * 100;

    // Clamp between full (0%) and peek
    const maxTranslate = this.snaps.peek + 2;   // tiny overscroll tolerance
    this.currentTranslate = Math.max(0, Math.min(maxTranslate, this.startTranslate + deltaPercent));
    this.el.style.transform = `translateY(${this.currentTranslate}%)`;

    // Update backdrop opacity (0 at peek, 1 at full)
    const peekPct = this.snaps.peek;
    const progress = Math.max(0, 1 - (this.currentTranslate / peekPct));
    if (this.backdrop) {
      this.backdrop.style.opacity = progress;
      this.backdrop.classList.toggle('visible', progress > 0.02);
    }
  },

  _onDragEnd() {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.el.classList.remove('no-transition');
    if (this.backdrop) this.backdrop.style.opacity = '';

    // Velocity-based snapping
    const elapsed = Date.now() - this.startTime;
    const deltaPercent = this.currentTranslate - this.startTranslate;
    const velocity = deltaPercent / elapsed;   // %/ms

    if (velocity > 0.12) {
      // Fast swipe down → dismiss the sheet entirely
      this._setState('hidden');
    } else if (velocity < -0.12) {
      // Fast swipe up → full
      this._setState('full');
    } else {
      this._snapToNearest(this.currentTranslate);
    }
  },

  _snapToNearest(pct) {
    let closestState = 'peek';
    let minDist = Infinity;

    for (const [name, val] of Object.entries(this.snaps)) {
      const dist = Math.abs(pct - val);
      if (dist < minDist) {
        minDist = dist;
        closestState = name;
      }
    }
    this._setState(closestState);
  },

  // --- Mouse handling (desktop) ---

  _onMouseDown(e) {
    this._computePeek();
    this.el.classList.add('state-open');   // above dock while dragging
    this.isDragging = true;
    this.startY = e.clientY;
    this.startTranslate = this.currentTranslate;
    this.startTime = Date.now();
    this.el.classList.add('no-transition');

    const onMouseMove = (e) => {
      if (!this.isDragging) return;
      const deltaY = e.clientY - this.startY;
      const sheetHeight = this._getSheetHeight();
      const deltaPercent = (deltaY / sheetHeight) * 100;
      const maxTranslate = this.snaps.peek + 2;
      this.currentTranslate = Math.max(0, Math.min(maxTranslate, this.startTranslate + deltaPercent));
      this.el.style.transform = `translateY(${this.currentTranslate}%)`;
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      this._onDragEnd();
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }
};

// ============================================================================
// Application Initialization
// ============================================================================

async function initApp() {
  console.log('[App] Initializing...');

  // 0. Handedness first: the radial wheel and the layer dial both read
  //    body.bw-left-handed when they are constructed, so the class has to be
  //    on the body before either exists — otherwise a left-handed user sees
  //    them appear on the right and jump across.
  document.body.classList.toggle('bw-left-handed', isLeftHanded());

  // 1. Initialize i18n
  initI18n();
  initLanguageSwitcher();

  // 1b. Authentication gate. Runs early so an unauthenticated visitor is
  //     blocked before the app becomes usable (development posture).
  await initAuth();

  // 2. Initialize PWA features (Service Worker, install prompt, offline detection)
  await initPWA({
    installButton: '#install-btn',
    offlineIndicator: '#offline-indicator',
    autoRegisterSW: true
  });

  // 3. Initialize the map
  mapView = new MapView('map', {
    center: [-1.5, 7.5],  // Ghana
    zoom: 7,
    basemap: 'osm'
  });

  // 3a. Apply the user's previously-chosen basemap, if any. Runs before the
  //     first frame so the user never sees the default OSM flash before the
  //     real selection appears.
  LayerPicker.restorePersisted(mapView);

  // 3b. Initialize map measurement tools (Circle, Line)
  mapTools = new MapTools(mapView.getMap());

  // Log measurement results
  mapTools.onMeasureComplete((result) => {
    console.log('[MapTools] Measurement complete:', result);
  });

  // Handle "Navigate to" POI requests (OSRM routing)
  mapView.onNavigateTo((poi) => {
    navigateToOSRM(poi);
  });

  // Set up map click handler immediately after map creation
  mapView.onClick((lon, lat, feature, evt) => {
    console.log('[App] Map clicked:', lon, lat, feature);

    // Show tap ripple at click position
    showMapTapRipple(evt.pixel);

    // Dismiss popups on any click
    mapView.hidePoiActionPopup();
    mapView.hideCirclePoiPopup();

    // ── Clicked a measurement circle → show POI count popup ──────
    if (feature && feature.get('_layerType') === 'measure_circle') {
      const circleGeom = feature.getGeometry();
      const poiSource = mapView.getPoiSource();
      const counts = countPOIsInCircle(circleGeom, poiSource);
      const radius = feature.get('_radius');
      mapView.showCirclePoiPopup(evt.coordinate, counts, radius);
      return;
    }

    // ── Clicked a weather-alert polygon → show that alert's detail ──────
    // Works in any mode (alerts sit below POI/markers, so pins win the hit
    // test and stay tappable; empty zone areas fall through to the polygon).
    if (feature && feature.get('featureType') === 'alert') {
      const id = feature.get('alertId');
      const all = getCachedAlerts();
      const alert = all.find((a) => a.id === id) || null;
      if (alert) showWeatherAlertsPanel({ focusId: id });
      return;
    }

    // Only handle location-related clicks in addLocation mode
    if (currentMode !== 'addLocation') {
      console.log('[App] Not in addLocation mode, skipping popup');
      return;
    }

    if (feature) {
      // Check if this is a cluster feature (from Cluster source)
      const clusterMembers = feature.get('features');
      if (clusterMembers) {
        if (clusterMembers.length === 1) {
          // Single-POI cluster → show action popup with the original feature
          mapView.showPoiActionPopup(clusterMembers[0], evt.coordinate);
        } else {
          // Multi-POI cluster → zoom in to expand
          mapView.zoomToCluster(feature);
        }
      } else if (feature.get('featureType') === 'poi') {
        // Direct POI feature (defensive fallback)
        mapView.showPoiActionPopup(feature, evt.coordinate);
      } else {
        // Clicked on a user marker → select it and open the edit form.
        // (All local locations belong to this device's user, so they are
        // editable now; when login lands, editing is gated by ownership.)
        mapView.selectMarker(feature);
        showLocationDetails(feature);
        mapView.showEditLocationPopup(feature);
      }
    } else {
      // Clicked on empty space - show add location popup at click position
      mapView.clearSelection();
      mapView.showAddLocationPopup(evt.coordinate);
    }
  });

  // Provide role-specific Add-Location extras (user-type fields + media
  // validation). Called each time the form opens, so it reflects the current
  // user type — which the future login will set via the same setUserType().
  mapView.setAddLocationFieldProvider(async () => {
    let typeId = 'visitor';
    try { typeId = await getUserType(); } catch (_) {}
    const def = getUserTypeDef(typeId);
    return {
      extraFields: getExtraLocationFields(typeId),
      defaultCategory: def.defaultCategory,
      mediaValidate: (file) => inspectMedia(file),
    };
  });

  // Set up handler for the map add location popup form
  mapView.onAddLocation(async (data) => {
    console.log('[App] Add location from map popup:', data);
    try {
      const result = await addLocation(data.name, data.lon, data.lat, {
        description: data.description || null,
        category: data.category || 'default'
      });
      console.log('[App] Location added:', data.name, 'id:', result.id);

      if (result.id) {
        // Persist user-type-specific attributes (menu specials, cuisine, …).
        if (data.attributes && Object.keys(data.attributes).length) {
          try { await saveLocationAttributes(result.id, data.attributes); }
          catch (e) { console.warn('[App] saveLocationAttributes failed:', e); }
        }
        // Persist an attached photo / short video (stored on-device in OPFS).
        if (data.media && data.media.file) {
          try {
            const meta = await saveMediaFile(data.media.file, {
              kind: data.media.kind, durationMs: data.media.durationMs,
            });
            await addLocationMedia({ locationId: result.id, ...meta });
          } catch (e) { console.warn('[App] media save failed:', e); }
        }
      }

      await loadLocations();

      // Zoom to the new location on the map
      mapView?.zoomTo(data.lon, data.lat, 14);

      // Select the new marker
      if (result.id) {
        mapView?.selectMarker(result.id);
      }

      showSuccess(t('locations.addedSuccess'));

    } catch (error) {
      console.error('[App] Failed to add location:', error);
      showError(t('locations.addFailed') + ': ' + error.message);
    }
  });

  // Hover thumbnail: resolve a location's first media to an object URL, cached
  // so repeated hovers don't re-read OPFS. Cache is invalidated on data change.
  const thumbCache = new Map();   // locationId → { url, kind } | null
  mapView.setThumbnailProvider(async (locationId) => {
    if (thumbCache.has(locationId)) return thumbCache.get(locationId);
    let thumb = null;
    try {
      const rows = await getLocationMedia(locationId);
      if (rows && rows.length) {
        const m = rows[0];
        thumb = { url: await getMediaObjectURL(m.filename), kind: m.kind };
      }
    } catch (_) {}
    thumbCache.set(locationId, thumb);
    return thumb;
  });
  onDatabaseChange((change) => {
    if (change.table === 'location_media') thumbCache.clear();
  });

  // Pre-fill data for the edit form: current attributes + first media preview.
  mapView.setEditLocationDataProvider(async (locationId) => {
    let attributes = {};
    let media = null;
    try { attributes = await getLocationAttributes(locationId); } catch (_) {}
    try {
      const rows = await getLocationMedia(locationId);
      if (rows && rows.length) {
        const m = rows[0];
        media = { url: await getMediaObjectURL(m.filename), kind: m.kind };
      }
    } catch (_) {}
    return { attributes, media };
  });

  // Save edits to an existing location.
  mapView.onEditLocation(async (data) => {
    console.log('[App] Edit location:', data);
    try {
      await updateLocation(data.id, {
        name: data.name,
        description: data.description || null,
        category: data.category || 'default',
      });
      if (data.attributes && Object.keys(data.attributes).length) {
        try { await saveLocationAttributes(data.id, data.attributes); }
        catch (e) { console.warn('[App] saveLocationAttributes (edit) failed:', e); }
      }
      // A newly-picked file replaces nothing automatically — it's added.
      if (data.media && data.media.file) {
        try {
          const meta = await saveMediaFile(data.media.file, {
            kind: data.media.kind, durationMs: data.media.durationMs,
          });
          await addLocationMedia({ locationId: data.id, ...meta });
        } catch (e) { console.warn('[App] media save (edit) failed:', e); }
      }
      await loadLocations();
      mapView?.selectMarker(data.id);
      showSuccess(t('editLocation.saved') || 'Changes saved');
    } catch (error) {
      console.error('[App] Failed to edit location:', error);
      showError((t('locations.addFailed') || 'Save failed') + ': ' + error.message);
    }
  });

  // 4. Initialize database (non-fatal — POI and map work without it)
  let dbOk = false;
  try {
    console.log('[App] Initializing database...');

    await initSchema();
    console.log('[App] Database ready');

    const status = await getDatabaseStatus();
    console.log('[App] Database status:', status);
    dbOk = true;

  } catch (error) {
    console.error('[App] Database initialization failed:', error);
    showError(t('error.dbInit'));
  }

  // 5. Initialize UI
  initUI();

  // 6. Load initial data and display on map (only if DB is ready)
  if (dbOk) {
    await loadLocations();

    // 7. Listen for database changes from other tabs
    onDatabaseChange((change) => {
      console.log('[App] Database change:', change);
      if (change.table === 'locations' && !change.local) {
        loadLocations();
      }
    });
  }

  // 8. Set up offline handling
  onOfflineChange((offline) => {
    if (offline) {
      console.log('[App] Working offline - data will sync when back online');
    } else {
      console.log('[App] Back online - syncing data...');
      syncData();
    }
  });

  // 9. Load POI layer from PostGIS API, then initialise the amenity filter
  //    (the toggles now live in the radial wheel's "Amenities" panel).
  loadPOI().then(() => initPoiFilter());

  // 10. Re-render locations list when language changes
  onLanguageChange(() => {
    if (dbOk) loadLocations();
    // Update MapView popup/form translations
    mapView?.updateI18n();
  });

  // 11. Initialise the mesh layer (Bitchat-compatible, currently over the
  //     SimulatedTransport — BLE and Capacitor transports slot in later).
  initMesh().catch((err) => console.error('[App] Mesh init failed:', err));

  // 12. Initialise GPS live-position + trail recording.
  initGpsTracking();

  // 13. Ambient presence tracking (consent-gated) for vicinity alerts.
  //     Kept as a promise rather than awaited here: the rest of the interface
  //     should finish building while the consent dialog is up. The start
  //     screen waits on it below.
  const presenceSettled = initPresenceTracking()
    .catch((err) => console.error('[App] Presence init failed:', err));

  // 14. Radial menu (Option B) — the consolidated right-edge control.
  initRadialMenu();

  // 15. Weather alerts (Ghana Met Agency) — map polygons + alert pill.
  initWeatherAlerts();

  // 16. Start screen (Option D). Deliberately last: it comes after signing in
  //     (step 1b) and after the location prompt has been answered, so the user
  //     is never asked two things at once.
  await presenceSettled;
  await maybeShowStartScreen();

  console.log('[App] Initialized successfully');
}

// ============================================================================
// Start screen — Option D from BisaWofa-Start-Screen-Options.docx
//
// Off by default until the Board has chosen. Flip START_SCREEN_ENABLED to try
// it; the user's own "Always start on the map" preference is separate and
// always wins.
// ============================================================================

const START_SCREEN_ENABLED = true;   // ← set false to go straight to the map

async function maybeShowStartScreen() {
  if (!START_SCREEN_ENABLED || startScreenDisabled()) return;

  // Tell the user what is waiting for them rather than a bare "Alerts".
  const alerts = getCachedAlerts();
  const top = alerts[0];
  const alertText = alerts.length
    ? (alerts.length === 1
        ? (top.event || top.headline || 'Weather alert')
        : `${alerts.length} weather alerts`)
    : '';

  let action = 'map';
  let query = '';
  try {
    ({ action, query } = await openStartScreen({ alertCount: alerts.length, alertText }));
  } catch (err) {
    console.warn('[App] Start screen failed; going to the map', err);
    return;
  }

  switch (action) {
    case 'ask': {
      // The question was typed on the start screen itself, so we run it rather
      // than handing the user over to the map's own search box. A conventional
      // place search for now — natural-language questions wait for the
      // assistant, per the staged path in the board paper.
      const found = await mapView?.runSearch(query);
      if (!found) showError(`Nothing found for “${query}”.`);
      break;
    }
    case 'near':
      mapView?.triggerLocateMe();
      break;
    case 'alerts':
      showAlertServicesPanel();
      break;
    case 'map':
    default:
      break;   // the map is already there
  }
}

// ============================================================================
// Authentication
//
// AUTH_GATE_MODE controls what happens when nobody is signed in:
//
//   'hard'  — the login is NOT dismissible; the app is unusable until the user
//             signs in. This is the current development posture: it blocks
//             people who open the app by chance, while known users (on the
//             server-side allow-list) sign in once and are then in.
//   'soft'  — the login can be dismissed and the app stays usable anonymously.
//             THIS IS THE INTENDED MODE FOR PUBLIC LAUNCH — flip the constant
//             below; nothing else needs to change.
//
// Offline safety: signing in needs the network, so if the session check itself
// fails to reach the server we do NOT hard-block — an offline user would
// otherwise be locked out of an offline-first app.
// ============================================================================

const AUTH_GATE_MODE = 'hard';   // ← change to 'soft' at public launch

/**
 * Local-development bypass.
 *
 * The bw_social.users table is owned by the database team and not yet seeded,
 * so no email can pass the gate. Until it is, skip the gate on the dev machine
 * so development can continue.
 *
 * This is SAFE IN PRODUCTION by construction:
 *   • `import.meta.env.DEV` is a compile-time literal — Vite replaces it with
 *     `false` in every production build, so the whole branch is stripped out by
 *     dead-code elimination and can never reach dist/.
 *   • The hostname check is a second, runtime guard for good measure.
 * Both must be true, so a production build served from localhost still gates.
 */
const AUTH_DEV_BYPASS =
  import.meta.env.DEV &&
  ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);

async function initAuth() {
  if (AUTH_DEV_BYPASS) {
    console.warn('[Auth] DEV bypass active (localhost) — authentication skipped');
    _reflectAuthInSettings();
    return;
  }

  const session = await checkSession();

  if (session.ok) {
    console.log('[Auth] Signed in as', session.email);
    _reflectAuthInSettings();
    return;
  }

  if (session.offline) {
    // Server unreachable — don't lock an offline user out of an offline app.
    console.warn('[Auth] Auth server unreachable; continuing without a gate');
    _reflectAuthInSettings();
    return;
  }

  const dismissible = AUTH_GATE_MODE !== 'hard';
  // In hard mode, keep re-presenting the dialog until the user signs in.
  // (It is non-dismissible, so this loop only turns on a failed/edge close.)
  for (;;) {
    const res = await openLoginDialog({ dismissible });
    if (res.ok) {
      console.log('[Auth] Signed in as', res.email);
      showSuccess(`Signed in as ${getDisplayName() || res.email}`);
      break;
    }
    if (dismissible) break;   // soft gate — user chose to browse anonymously
  }
  _reflectAuthInSettings();
}

/** Show the signed-in user (and a sign-out action) in the Settings panel. */
function _reflectAuthInSettings() {
  const row = document.getElementById('auth-status-row');
  const label = document.getElementById('auth-status-email');
  const btn = document.getElementById('auth-signout-btn');
  if (!row || !label || !btn) return;

  const email = getCurrentEmail();
  const user  = getCurrentUser();
  const name  = user?.display_name || user?.username || null;

  // Prefer the profile name from bw_social.users, keeping the email visible
  // underneath so the user can tell which account they are signed in with.
  if (name && email) {
    label.innerHTML = '';
    const strong = document.createElement('div');
    strong.textContent = name;
    strong.style.fontWeight = '600';
    const sub = document.createElement('div');
    sub.textContent = email;
    sub.style.cssText = 'font-size:0.82em;opacity:0.7;';
    label.append(strong, sub);
  } else {
    label.textContent = email || 'Not signed in';
  }
  btn.hidden = !isSignedIn();

  if (!btn._wired) {
    btn._wired = true;
    btn.addEventListener('click', async () => {
      await logout();
      showSuccess('Signed out');
      _reflectAuthInSettings();
      // Re-assert the gate immediately in hard mode.
      if (AUTH_GATE_MODE === 'hard') initAuth();
    });
  }
}

// ============================================================================
// Radial Menu (Option B — "Show existing" upper arc / "Create new" lower arc)
//
// The consolidated right-edge control. Replaces the saved-locations chevron,
// the Add-Location FAB, and the ls-locate (Locate + Record) control — their
// actions all live here now.
// ============================================================================

let radialMenu = null;
let radialDial = null;

// Handedness: right-handed by default; left-handed docks the wheel on the left
// edge. A pure UI preference, stored in localStorage like fieldwork/font-size
// so it can be read synchronously when the wheel is built.
const LEFT_HANDED_STORAGE_KEY = 'bisawofa-left-handed';
function isLeftHanded() { return localStorage.getItem(LEFT_HANDED_STORAGE_KEY) === '1'; }

function initRadialMenu() {
  const mapEl = mapView?.getMap()?.getTargetElement();
  if (!mapEl) return;

  // Labels are English literals for now (radial.* i18n keys are a follow-up;
  // a missing key would render the raw key on the wheel since t() returns the
  // key as its miss fallback).
  radialMenu = new RadialMenu({
    target: mapEl,
    hubImage: `${import.meta.env.BASE_URL}img/bw-192x192.png`,
    hubLabel: 'Menu',
    side: isLeftHanded() ? 'left' : 'right',
    sections: [
      {
        key: 'show', arc: 'upper', color: '#556B2F',
        items: [
          {
            id: 'saved', icon: 'bi-geo-alt-fill', label: 'Places',
            onSelect: () => {
              const el = document.getElementById('offcanvasRight');
              if (el) (Offcanvas.getInstance(el) || new Offcanvas(el)).show();
            },
          },
          {
            id: 'tracks', icon: 'bi-signpost-split', label: 'Tracks',
            onSelect: () => showTracksList(),
          },
          {
            id: 'amenities', icon: 'bi-shop', label: 'Amenities',
            onSelect: () => showPoiFilterPanel(),
          },
          {
            id: 'alerts', icon: 'bi-exclamation-triangle', label: 'Alerts',
            onSelect: () => showAlertServicesPanel(),
          },
        ],
      },
      {
        key: 'create', arc: 'lower', color: '#8B008B',
        items: [
          {
            id: 'add', icon: 'bi-plus-lg', label: 'Place',
            onSelect: () => {
              const center = mapView?.getMap()?.getView()?.getCenter();
              if (center) mapView.showAddLocationPopup(center);
            },
          },
          {
            id: 'record', icon: 'bi-record-circle', label: 'Track',
            onSelect: () => mapView?.triggerToggleRecording(!mapView.isRecordingTrail),
          },
          {
            id: 'locate', icon: 'bi-crosshair', label: 'Locate',
            onSelect: () => mapView?.triggerLocateMe(),
          },
        ],
      },
    ],
  });

  // Reflect recording state on the Record item (red pulse + Stop icon/label).
  mapView.onRecordingStateChange((recording) => {
    radialMenu?.setItemActive('record', recording);
    radialMenu?.setItem('record', {
      icon: recording ? 'bi-stop-fill' : 'bi-record-circle',
      label: recording ? 'Stop' : 'Track',
    });
  });
}

/**
 * Simple read-only list of recorded GPS tracks (Show existing → Tracks),
 * rendered as a frosted-glass card.
 */
async function showTracksList() {
  injectGlassStyles();
  let trails = [];
  try { await dbReady; trails = await getGpsTrails(); } catch (_) {}

  const rows = trails.length
    ? trails.map((tr) => {
        const when = tr.started_at ? new Date(tr.started_at).toLocaleString() : '';
        const dist = tr.distance_m != null ? formatDistance(tr.distance_m) : '';
        const pts = tr.point_count != null ? `${tr.point_count} pts` : '';
        const meta = [pts, dist].filter(Boolean).join(' · ');
        return `
          <div style="padding:10px 4px; border-bottom:1px solid rgba(0,0,0,0.08);">
            <div style="font-weight:600; color:#1c1020;">${escapeHtml(tr.name || 'Track')}</div>
            <div style="font-size:0.8rem; color:rgba(40,20,45,0.6);">${escapeHtml(when)}${meta ? ' · ' + escapeHtml(meta) : ''}</div>
          </div>`;
      }).join('')
    : `<div style="padding:16px 4px; color:rgba(40,20,45,0.6); font-style:italic;">No tracks recorded yet.</div>`;

  const overlay = document.createElement('div');
  overlay.className = 'glass-overlay';
  overlay.innerHTML = `
    <div class="glass-card" role="dialog" aria-modal="true">
      <div class="glass-grabber" aria-hidden="true"></div>
      <header class="glass-header">
        <div class="glass-icon" aria-hidden="true"><i class="bi bi-signpost-split"></i></div>
        <div>
          <h2 class="glass-title">${escapeHtml('Recorded tracks')}</h2>
          <p class="glass-subtitle">${trails.length} ${trails.length === 1 ? 'track' : 'tracks'}</p>
        </div>
      </header>
      <div class="glass-body" style="max-height:50vh; overflow:auto;">${rows}</div>
      <footer class="glass-footer">
        <button type="button" class="glass-btn glass-btn-primary" data-close>${escapeHtml(t('status.close') || 'Close')}</button>
      </footer>
    </div>`;
  document.body.appendChild(overlay);
  requestAnimationFrame(() => overlay.classList.add('glass-open'));
  const close = () => {
    overlay.classList.remove('glass-open');
    setTimeout(() => overlay.remove(), 350);
  };
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay || e.target.closest('[data-close]')) close();
  });
}

// ============================================================================
// Ambient presence tracking (vicinity alerts / recommendations)
//
// Consent-gated, distance-based, low-frequency location tracking. Stores fixes
// locally (location_fixes) and syncs to the server in batches. Background
// (app-closed) tracking is a native-only follow-up; in the browser this runs
// while the app is open.
// ============================================================================

const PRESENCE_CONSENT_KEY = 'presence_tracking_consent';   // 'granted' | 'denied'
let presenceTracker = null;
let presenceSyncTimer = null;

async function initPresenceTracking() {
  await dbReady;

  presenceTracker = new PresenceTracker({
    storage: { addFix: (fix) => addLocationFix(fix) },
  });

  if (!presenceTracker.isSupported) {
    console.warn('[Presence] Geolocation not supported — presence tracking disabled');
    return;
  }

  const consent = await getSetting(PRESENCE_CONSENT_KEY);

  // What the answer ends up being — `consent` is only what it was on entry.
  let effective = consent;

  if (consent === 'granted') {
    _startPresence();
  } else if (consent == null) {
    // Never asked — request consent once, after the map has settled. This is
    // awaited rather than fired and forgotten, so that whatever comes next
    // (the start screen) does not appear on top of the question.
    await new Promise((r) => setTimeout(r, 1200));
    effective = (await requestPresenceConsent()) ? 'granted' : 'denied';
  }
  // consent === 'denied' → do nothing until the user enables it in Settings.

  // Wire the revocable Settings toggle (offcanvasLeft), if present. This runs
  // after the dialog now, so it must use the answer rather than the value we
  // started with — otherwise granting consent would leave the toggle off.
  _wirePresenceSettingToggle(effective === 'granted');
}

/** Show the consent dialog and act on the choice. */
async function requestPresenceConsent() {
  const ok = await openGlassDialog({
    title: 'Share your location?',
    subtitle: 'For nearby alerts & recommendations',
    icon: 'bi-geo-alt',
    body:
      'BisaWofa can keep track of roughly where you are so it can warn you about ' +
      'emergencies nearby and suggest useful places around you.\n' +
      'Your location is sampled sparingly to save battery and data, stored on your ' +
      'device, and synced to BisaWofa. You can turn this off any time in Settings.',
    confirmLabel: 'Allow',
    cancelLabel: 'Not now',
  });

  await setSetting(PRESENCE_CONSENT_KEY, ok ? 'granted' : 'denied');
  _wirePresenceSettingToggle(ok);
  if (ok) {
    _startPresence();
    showSuccess('Location tracking enabled');
  }
  return ok;
}

function _startPresence() {
  if (!presenceTracker || presenceTracker.isRunning) return;
  try { presenceTracker.start(); } catch (err) { console.warn('[Presence] start failed:', err); return; }

  // Periodic batch sync (every 2 min) + immediate flush when back online.
  const flush = () => syncLocationFixes().then((r) => {
    if (r.pushed) console.log(`[Presence] Synced ${r.pushed} fix(es)`);
  }).catch(() => {});
  flush();
  clearInterval(presenceSyncTimer);
  presenceSyncTimer = setInterval(flush, 120000);
  onOfflineChange((offline) => { if (!offline) flush(); });
}

function _stopPresence() {
  presenceTracker?.stop();
  clearInterval(presenceSyncTimer);
  presenceSyncTimer = null;
}

/** Reflect + wire the revocable "Nearby alerts" toggle in the settings panel. */
function _wirePresenceSettingToggle(isOn) {
  const toggle = document.getElementById('presence-toggle');
  if (!toggle) return;
  toggle.checked = !!isOn;
  if (toggle._wired) return;
  toggle._wired = true;
  toggle.addEventListener('change', async () => {
    if (toggle.checked) {
      await setSetting(PRESENCE_CONSENT_KEY, 'granted');
      _startPresence();
      showSuccess('Location tracking enabled');
    } else {
      await setSetting(PRESENCE_CONSENT_KEY, 'denied');
      _stopPresence();
      showSuccess('Location tracking turned off');
    }
  });
}

/**
 * Initialise MeshClient + NearbyPanel and keep the dock-button badge in
 * sync with the live peer count. Kept outside initApp so it can be awaited
 * or retried independently.
 */
async function initMesh() {
  const mesh = getMesh();

  // Transport selection:
  //   - Native (Capacitor) build → simulated + capacitor-ble. The web
  //     Bluetooth transport is unusable on iOS Safari; Capacitor's native
  //     plugin is the production path.
  //   - Browser, ?ble=1 → simulated + web-bluetooth. For dev testing
  //     against a real Bitchat peer from Chrome/Brave on a desktop.
  //   - Browser, no ?ble=1 → simulated only with ephemeral per-tab
  //     identity (two-tab dev smoke test).
  let isNative = false;
  try {
    const { Capacitor } = await import('@capacitor/core');
    isNative = Capacitor.isNativePlatform();
  } catch (_) { /* @capacitor/core not present in pure-web dev runs */ }

  const urlParams = new URLSearchParams(window.location.search);
  const wantWebBle = urlParams.has('ble');
  let transports;
  if (isNative) {
    // Full dual-role mesh node: central (scan/connect) + peripheral
    // (advertise/serve). The peripheral transport degrades gracefully if the
    // device can't advertise, so it's safe to always request it on native.
    transports = ['simulated', 'capacitor-ble', 'capacitor-peripheral'];
  } else if (wantWebBle) {
    transports = ['simulated', 'web-bluetooth'];
  } else {
    transports = ['simulated'];
  }

  await mesh.init({ transports });
  initNearbyPanel(mesh);

  const badge = document.getElementById('dock-btn-nearby-badge');
  const updateBadge = () => {
    if (!badge) return;
    const count = mesh.getPeers().length;
    badge.textContent = String(count);
    badge.style.display = count > 0 ? '' : 'none';
  };
  mesh.onPeer(updateBadge);
  mesh.onPeerLost(updateBadge);
  updateBadge();

  // Polite shutdown on page unload so peers see us leave promptly
  window.addEventListener('pagehide', () => { mesh.stop(); });
}

// ============================================================================
// GPS live-position + trail recording
//
// Wiring only — all GPS logic lives in the reusable src/geotracker/ engine and
// the BisaWofa adapter in src/geotracker-bisawofa.js. Here we connect the
// engine's events to the map's render/control hooks and the optional navbar
// readout (each readout element is guarded, so it's fine if it's absent).
// ============================================================================

function initGpsTracking() {
  const readout  = document.getElementById('gps-readout');
  const coordsEl = document.getElementById('gps-coords');
  const accEl    = document.getElementById('gps-accuracy');
  const satsEl   = document.getElementById('gps-sats');

  if (!geoTracker.isSupported) {
    if (coordsEl) coordsEl.textContent = 'No GPS';
    console.warn('[GPS] Geolocation not supported in this browser');
    return;
  }

  // Live readout — fires for every fix (one-shot Locate or watch).
  geoTracker.on('position', (fix) => {
    if (coordsEl) coordsEl.textContent = `${formatCoord(fix.lat)}, ${formatCoord(fix.lon)}`;
    if (accEl)    accEl.textContent = formatAccuracy(fix.accuracy);
    if (satsEl)   satsEl.textContent = `${fix.satellites != null ? fix.satellites : '—'} sat`;
    if (readout) {
      readout.classList.add('active');
      readout.classList.remove('quality-good', 'quality-fair', 'quality-poor');
      readout.classList.add('quality-' + accuracyQuality(fix.accuracy));
    }
    mapView?.showCurrentPosition(fix.lon, fix.lat, fix.accuracy);
  });

  // Each recorded waypoint extends the on-map trail line.
  geoTracker.on('point', (evt) => {
    mapView?.appendTrailPoint(evt.point.lon, evt.point.lat);
  });

  geoTracker.on('error', (err) => {
    console.warn('[GPS]', err?.message || err);
    if (err && err.code === 1) { // PERMISSION_DENIED
      showError('Location permission denied. Enable location access to use GPS.');
    }
  });

  // "Locate me" → one-shot position + recenter.
  mapView.onLocateMe(async () => {
    try {
      const fix = await geoTracker.getCurrentPosition();
      mapView.centerOn(fix.lon, fix.lat, 16);
    } catch (err) {
      showError('Could not get your location: ' + (err?.message || err));
    }
  });

  // "Record trail" → start/stop. Recording persists locally (no server sync yet).
  mapView.onToggleRecording(async (start) => {
    if (start) {
      try {
        await dbReady;
        mapView.startTrailRender();
        mapView.setRecordingState(true);
        readout?.classList.add('recording');
        await geoTracker.startRecording({ name: `Trail ${new Date().toLocaleString()}` });
        showSuccess('GPS trail recording started');
      } catch (err) {
        mapView.setRecordingState(false);
        readout?.classList.remove('recording');
        showError('Could not start recording: ' + (err?.message || err));
      }
    } else {
      try {
        const res = await geoTracker.stopRecording();
        mapView.setRecordingState(false);
        readout?.classList.remove('recording');
        if (res) {
          showSuccess(`Trail saved: ${res.pointCount} points, ${formatDistance(res.distanceM)}`);

          // Capture descriptive metadata for this track. The form is the
          // Apple-glass modal; saving links a uniquely-id'd metadata record to
          // the trail so we know who entered which track.
          try {
            const meta = await openTrailMetaForm({
              trailId: res.trailId,
              summary: { pointCount: res.pointCount, distanceM: res.distanceM },
            });
            if (meta) {
              const { metaUuid } = await saveGpsTrailMeta({ trailId: res.trailId, ...meta });
              console.log(`[GPS] Trail ${res.trailId} metadata saved (${metaUuid})`);
              showSuccess('Track details saved');
            }
          } catch (metaErr) {
            console.warn('[GPS] Could not save track details:', metaErr);
            showError('Could not save track details: ' + (metaErr?.message || metaErr));
          }
        }
      } catch (err) {
        showError('Error stopping recording: ' + (err?.message || err));
      }
    }
  });
}

// ============================================================================
// Language Switcher
// ============================================================================

function initLanguageSwitcher() {
  const select = document.getElementById('language-select');
  if (!select) return;

  // Set initial value
  select.value = getLanguage();

  select.addEventListener('change', (e) => {
    setLanguage(e.target.value);
  });

  // Keep select in sync when language changes programmatically
  onLanguageChange((lang) => {
    select.value = lang;
  });
}

// ============================================================================
// UI Initialization
// ============================================================================

function initUI() {
  // Export button
  const exportBtn = document.getElementById('export-btn');
  if (exportBtn) {
    exportBtn.addEventListener('click', handleExport);
  }

  // GeoJSON Export button
  const exportGeoJSONBtn = document.getElementById('exportGeoJSON-btn');
  if (exportGeoJSONBtn) {
    exportGeoJSONBtn.addEventListener('click', handleExportGeoJSON);
  }

  // Status button
  const statusBtn = document.getElementById('status-btn');
  if (statusBtn) {
    statusBtn.addEventListener('click', handleShowStatus);
  }

  // Fit to markers button
  const fitBtn = document.getElementById('fit-btn');
  if (fitBtn) {
    fitBtn.addEventListener('click', () => mapView?.fitToMarkers());
  }

  // ============================================
  // Mode Selector & Measurement Tools (Bottom Dock)
  // ============================================

  const fabBtn = document.getElementById('fab-add-location');
  const measureCircleBtn = document.getElementById('dock-btn-measure-circle');
  const measureLineBtn = document.getElementById('dock-btn-measure-line');
  const clearBtn = document.getElementById('dock-btn-clear');

  // Dock measurement mode buttons (mutually exclusive .active)
  const dockModeButtons = [measureCircleBtn, measureLineBtn];

  // Helper to set active mode and update button + FAB states
  const setMode = (mode, activeBtn) => {
    console.log('[setMode] Changing mode from', currentMode, 'to', mode);
    currentMode = mode;

    // Update dock button active states
    dockModeButtons.forEach(btn => {
      if (btn) btn.classList.toggle('active', btn === activeBtn);
    });

    // FAB: dim when a measurement tool is active, bright otherwise
    fabBtn?.classList.toggle('dimmed', mode !== 'addLocation');

    // Deactivate any measurement tool when switching modes
    mapTools?.deactivate();

    // Hide popups when switching modes
    if (mode !== 'addLocation') {
      mapView?.hideAddLocationPopup();
    }
    mapView?.hideCirclePoiPopup();

    // Disable hover popups while a measurement tool is active
    if (mapView) {
      mapView.hoverPopupDisabled = mode.startsWith('measure');
      if (mapView.hoverPopupDisabled) mapView.hidePopup();
    }

    // Activate the appropriate tool for the new mode
    switch (mode) {
      case 'measureCircle':
        mapTools?.startCircleMeasure();
        break;
      case 'measureLine':
        mapTools?.startLineMeasure();
        break;
      // addLocation mode doesn't need tool activation
    }
  };

  // FAB — Add Location (primary CTA)
  if (fabBtn) {
    fabBtn.addEventListener('click', () => {
      setMode('addLocation', null);
    });
  }

  // Circle measurement button
  if (measureCircleBtn) {
    measureCircleBtn.addEventListener('click', () => {
      if (currentMode === 'measureCircle') {
        // Toggle off - return to addLocation mode
        setMode('addLocation', null);
      } else {
        setMode('measureCircle', measureCircleBtn);
      }
    });
  }

  // Line measurement button
  if (measureLineBtn) {
    measureLineBtn.addEventListener('click', () => {
      if (currentMode === 'measureLine') {
        setMode('addLocation', null);
      } else {
        setMode('measureLine', measureLineBtn);
      }
    });
  }

  // Clear button - clears measurements but stays in current mode
  if (clearBtn) {
    clearBtn.addEventListener('click', () => {
      mapTools?.clearMeasurements();
      mapView?.hideCirclePoiPopup();
      // If in a measurement mode, restart the tool
      if (currentMode.startsWith('measure')) {
        mapTools?.deactivate();
        switch (currentMode) {
          case 'measureCircle':
            mapTools?.startCircleMeasure();
            break;
          case 'measureLine':
            mapTools?.startLineMeasure();
            break;
        }
      }
    });
  }

  // (The on-map ol-ext GeolocationButton above the zoom + provides the
  // "Locate me" action; the duplicate dock entry has been removed to keep
  // the bottom dock fitting on small iPhone screens.)

  // Settings now lives in offcanvasLeft and is opened by the left-edge
  // cogwheel toggle (declared in index.html with the data-bs-toggle attrs).
  // The dock-btn-settings entry has been removed to free dock space.

  // Basemap switching is now the RadialDial (Design D — Radial Dial), a
  // corner dial on the map itself, so the dock button has been removed.
  // LayerPicker (Design B) is still imported for restorePersisted(), which
  // applies the saved basemap before first paint; both write the same key.
  radialDial = new RadialDial(mapView, { target: mapView.getMap().getTargetElement() });

  // Initialize the draggable bottom sheet (starts at peek = handle visible)
  bottomSheet.init();

  // Wire fieldwork mode toggle in the settings sheet
  wireFieldworkToggle();

  // Wire left-handed (mirror the radial wheel) toggle
  wireLeftHandedToggle();

  // Wire the start-screen toggle. The checkbox on the start screen itself only
  // turns it off; this is the way back on.
  const startToggle = document.getElementById('start-screen-toggle');
  if (startToggle) {
    startToggle.checked = !startScreenDisabled();
    startToggle.addEventListener('change', () => {
      setStartScreenDisabled(!startToggle.checked);
    });
  }

  // Wire the Credits (open-source attributions) button
  document.getElementById('credits-btn')
    ?.addEventListener('click', () => openCreditsModal());

  // Wire font size stepper and apply saved size
  applyFontSize(getFontSizeStep());
  wireFontSizeControl();

  // Wire the user-type selector (role — seeds the future login profile)
  wireUserTypeSelector();

  // Voice input (Web Speech API) — progressive enhancement
  initVoiceInput();
}

/**
 * Populate + wire the "I am a…" user-type selector in Settings. The chosen
 * type is stored in the local profile (app_settings) and drives the extra
 * fields in the Add-Location form. When login is implemented it will write the
 * same setting, so this selector becomes a reflection of the signed-in role.
 */
async function wireUserTypeSelector() {
  const sel = document.getElementById('user-type-select');
  if (!sel) return;
  sel.innerHTML = getUserTypeOptions()
    .map((o) => `<option value="${o.value}">${escapeHtml(o.label)}</option>`)
    .join('');
  try { sel.value = await getUserType(); } catch (_) {}
  sel.addEventListener('change', async () => {
    try { await setUserType(sel.value); } catch (e) { console.warn('[App] setUserType failed:', e); }
  });
}

// ============================================================================
// Location Handlers
// ============================================================================

async function handleAddLocation(event) {
  event.preventDefault();

  const form = event.target;
  const formData = new FormData(form);

  const name = formData.get('name');
  const longitude = parseFloat(formData.get('longitude'));
  const latitude = parseFloat(formData.get('latitude'));
  const description = formData.get('description') || null;
  const category = formData.get('category') || 'default';

  if (!name || isNaN(longitude) || isNaN(latitude)) {
    showError(t('error.requiredFields'));
    return;
  }

  try {
    const result = await addLocation(name, longitude, latitude, { description, category });
    console.log('[App] Location added:', name, 'id:', result.id);

    form.reset();
    await loadLocations();

    mapView?.zoomTo(longitude, latitude, 14);

    if (result.id) {
      mapView?.selectMarker(result.id);
    }

    showSuccess(t('locations.addedSuccess'));

  } catch (error) {
    console.error('[App] Failed to add location:', error);
    showError(t('locations.addFailed') + ': ' + error.message);
  }
}

async function loadLocations() {
  try {
    console.log('[App] Loading locations...');
    const locations = await getLocations();
    console.log('[App] Locations loaded:', locations);

    // Update the list
    renderLocations(locations);

    // Update the map markers
    if (mapView) {
      mapView.clearMarkers();
      if (locations.length > 0) {
        mapView.addMarkers(locations);
        console.log('[App] Added', locations.length, 'markers to map');
      }
    }

    // Update count display
    const countEl = document.getElementById('location-count');
    if (countEl) {
      countEl.textContent = locations.length;
    }

  } catch (error) {
    console.error('[App] Failed to load locations:', error);
  }
}

/**
 * Show details for a selected location
 */
function showLocationDetails(feature) {
  const name = feature.get('name');
  const description = feature.get('description');
  const category = feature.get('category');
  const lon = feature.get('lon') || feature.get('longitude');
  const lat = feature.get('lat') || feature.get('latitude');

  console.log('[App] Selected location:', { name, description, category, lon, lat });
}

function renderLocations(locations) {
  const container = document.getElementById('locations-list');
  if (!container) return;

  // Also update mobile count
  const mobileCount = document.getElementById('location-count-mobile');
  if (mobileCount) {
    mobileCount.textContent = locations.length;
  }

  if (locations.length === 0) {
    container.innerHTML = `
      <div class="text-center py-4 px-3">
        <div style="font-size:3rem; margin-bottom:8px; opacity:0.6;">🗺️</div>
        <p class="mb-1 fw-semibold" style="color:var(--bw-primary);">
          ${escapeHtml(t('locations.empty'))}
        </p>
        <p class="text-muted small mb-3">${escapeHtml(t('locations.emptyHint'))}</p>
        <button type="button" class="btn btn-accent btn-sm" id="empty-state-locate-btn">
          <i class="bi bi-geo-alt me-1"></i>${escapeHtml(t('dock.locate'))}
        </button>
      </div>
    `;
    // Wire the CTA to trigger geolocation + close the offcanvas
    const locateBtn = document.getElementById('empty-state-locate-btn');
    if (locateBtn) {
      locateBtn.addEventListener('click', () => {
        // Close the right offcanvas
        const offcanvasEl = document.getElementById('offcanvasRight');
        if (offcanvasEl) {
          const instance = Offcanvas.getInstance(offcanvasEl);
          if (instance) instance.hide();
        }
        // Trigger geolocation via the new GPS control
        mapView?.triggerLocateMe();
      });
    }
    return;
  }

  // Category emoji mapping
  const categoryEmojis = {
    'water': '💧',
    'school': '🏫',
    'health': '🏥',
    'market': '🏪',
    'hotel': '🏨',
    'restaurant': '🍽',
    'default': '📍',
    'other': '📌'
  };

  container.innerHTML = locations.map(loc => {
    const emoji = categoryEmojis[loc.category] || '📍';
    const categoryLabel = getCategoryLabel(loc.category);
    return `
      <a href="#" class="list-group-item list-group-item-action location-item py-2"
         data-id="${loc.id}" data-lon="${loc.longitude}" data-lat="${loc.latitude}">
        <div class="d-flex w-100 justify-content-between align-items-start">
          <div class="location-item-info">
            <h6 class="mb-1">${emoji} ${escapeHtml(loc.name)}</h6>
            <small class="text-muted font-monospace">${loc.latitude.toFixed(5)}, ${loc.longitude.toFixed(5)}</small>
          </div>
          <div class="d-flex align-items-center gap-2">
            <span class="badge badge-${loc.category}">${escapeHtml(categoryLabel)}</span>
            <button type="button" class="location-delete-btn" data-id="${loc.id}"
                    aria-label="Delete location" title="Delete">
              <i class="bi bi-trash"></i>
            </button>
          </div>
        </div>
        ${loc.description ? `<small class="text-secondary d-block mt-1">${escapeHtml(loc.description)}</small>` : ''}
      </a>
    `;
  }).join('');

  // Add click handlers to zoom to location
  container.querySelectorAll('.location-item').forEach(item => {
    item.addEventListener('click', (e) => {
      // Ignore clicks on the delete button
      if (e.target.closest('.location-delete-btn')) return;

      e.preventDefault();
      const lon = parseFloat(item.dataset.lon);
      const lat = parseFloat(item.dataset.lat);
      const id = parseInt(item.dataset.id);

      mapView?.zoomTo(lon, lat, 14);
      mapView?.selectMarker(id);
    });
  });

  // Wire delete buttons
  container.querySelectorAll('.location-delete-btn').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();

      const id = parseInt(btn.dataset.id);
      const item = btn.closest('.location-item');
      const name = item?.querySelector('h6')?.textContent?.trim() || '';

      if (!confirm(`Delete "${name}"?`)) return;

      try {
        await deleteLocation(id);
        mapView?.removeMarker(id);
        await loadLocations();
        showSuccess(t('locations.deleted'));
      } catch (err) {
        console.error('[App] Delete location error:', err);
        showError(t('locations.deleteError'));
      }
    });
  });
}

// ============================================================================
// Export Handler
// ============================================================================

async function handleExport() {
  try {
    await downloadDatabase('bisawofa-backup.sqlite3');
    showSuccess(t('locations.export') + ' - OK');
  } catch (error) {
    console.error('[App] Export failed:', error);
    showError(t('error.export') + ': ' + error.message);
  }
}

// Export as GeoJSON file
async function handleExportGeoJSON() {
  try {
	  const geojson = await exportToGeoJSON();

	  // Download as file
	  const blob = new Blob([JSON.stringify(geojson, null, 2)], { type: 'application/json' });
	  const url = URL.createObjectURL(blob);
	  const a = document.createElement('a');
	  a.href = url;
	  a.download = 'locations.geojson';
	  a.click();
	  URL.revokeObjectURL(url);

		showSuccess(`${t('locations.exportGeoJSON')} - ${geojson.features.length} location(s)`);
	}catch (error) {
    console.error('[App] GeoJSON Export failed:', error);
    showError(t('error.export') + ': ' + error.message);
  }
}

// ============================================================================
// Status Handler
// ============================================================================

/**
 * Database status, rendered in the standard frosted-glass look (replaces the
 * old Bootstrap modal — see BisaWofa-Form-Design-Principle.md).
 */
async function handleShowStatus() {
  try {
    const status = await getDatabaseStatus();
    injectGlassStyles();
    _ensureStatusStyles();

    const pill = (text, tone) =>
      `<span class="status-pill status-pill-${tone}">${escapeHtml(String(text))}</span>`;
    const row = (label, valueHtml) => `
      <div class="status-row">
        <span class="status-row-label">${escapeHtml(label)}</span>
        <span class="status-row-value">${valueHtml}</span>
      </div>`;

    const body =
      row(t('status.ready'), pill(status.ready ? t('status.yes') : t('status.no'), status.ready ? 'ok' : 'bad')) +
      row(t('status.online'), pill(isOnline() ? t('status.yes') : t('status.offline'), isOnline() ? 'ok' : 'warn')) +
      row(t('status.database'), `<code class="status-code">${escapeHtml(status.databasePath || 'N/A')}</code>`) +
      row(t('status.tables'), (status.tables || []).map((tbl) => pill(tbl, 'neutral')).join(' ')) +
      row(t('status.locations'), pill(status.locationCount, 'brand'));

    const overlay = document.createElement('div');
    overlay.className = 'glass-overlay';
    overlay.innerHTML = `
      <div class="glass-card" role="dialog" aria-modal="true">
        <div class="glass-grabber" aria-hidden="true"></div>
        <header class="glass-header">
          <div class="glass-icon" aria-hidden="true"><i class="bi bi-database-check"></i></div>
          <div>
            <h2 class="glass-title">${escapeHtml(t('status.title'))}</h2>
            <p class="glass-subtitle">${escapeHtml(t('status.database'))}</p>
          </div>
        </header>
        <div class="glass-body">${body}</div>
        <footer class="glass-footer">
          <button type="button" class="glass-btn glass-btn-primary" data-close>${escapeHtml(t('status.close'))}</button>
        </footer>
      </div>`;
    document.body.appendChild(overlay);
    requestAnimationFrame(() => overlay.classList.add('glass-open'));

    const close = () => {
      overlay.classList.remove('glass-open');
      setTimeout(() => overlay.remove(), 350);
    };
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay || e.target.closest('[data-close]')) close();
    });

  } catch (error) {
    console.error('[App] Failed to get status:', error);
    showError(t('error.export'));
  }
}

let _statusStyled = false;
function _ensureStatusStyles() {
  if (_statusStyled) return;
  _statusStyled = true;
  const css = `
    .status-row {
      display:flex; align-items:flex-start; justify-content:space-between; gap:12px;
      padding:9px 2px; border-bottom:1px solid rgba(0,0,0,0.07);
    }
    .status-row:last-child { border-bottom:none; }
    .status-row-label { font-size:0.84rem; font-weight:600; color:rgba(40,20,45,0.62); }
    .status-row-value { text-align:right; }
    .status-pill {
      display:inline-block; padding:2px 9px; border-radius:999px;
      font-size:0.76rem; font-weight:700; margin:1px 0 1px 3px;
    }
    .status-pill-ok      { background:rgba(46,125,50,0.15);  color:#2e7d32; }
    .status-pill-bad     { background:rgba(192,57,43,0.15);  color:#c0392b; }
    .status-pill-warn    { background:rgba(245,158,11,0.18); color:#a16207; }
    .status-pill-neutral { background:rgba(40,20,45,0.10);   color:#4a2a4a; }
    .status-pill-brand   { background:rgba(139,0,139,0.14);  color:#8B008B; }
    .status-code { font-size:0.76rem; color:rgba(40,20,45,0.75); word-break:break-all; }
    @media (prefers-color-scheme: dark) {
      .status-row { border-bottom-color: rgba(255,255,255,0.1); }
      .status-row-label { color: rgba(245,238,247,0.6); }
      .status-code { color: rgba(245,238,247,0.75); }
      .status-pill-neutral { background:rgba(255,255,255,0.14); color:#e8dcea; }
    }
  `;
  const el = document.createElement('style');
  el.textContent = css;
  document.head.appendChild(el);
}

// ============================================================================
// POI Layer (PostGIS / OSM data)
// ============================================================================

/**
 * Load POI features from the PHP REST API.
 * Fetches GeoJSON from /api/poi.php and adds to the POI layer.
 */
async function loadPOI() {
  if (!mapView) return;

  const toast = document.getElementById('poi-loading-toast');
  const toastText = toast?.querySelector('.poi-loading-text');

  try {
    console.log('[App] Loading POI data...');

    // Show loading toast
    if (toast) {
      toast.classList.remove('done');
      if (toastText) toastText.textContent = t('general.loading');
      toast.classList.add('visible');
    }

    const count = await mapView.loadPOI(`${import.meta.env.BASE_URL}api/poi.php`);
    console.log(`[App] POI loaded: ${count} features`);

    // Show success in toast, then fade out
    if (toast) {
      toast.classList.add('done');
      if (toastText) toastText.textContent = `${count} POI`;
      setTimeout(() => toast.classList.remove('visible'), 2000);
    }
  } catch (error) {
    // POI loading is non-critical - log but don't show error to user
    console.warn('[App] POI loading failed (API may not be running):', error.message);

    // Hide toast on failure
    if (toast) {
      toast.classList.remove('visible');
    }
  }
}

// ============================================================================
// POI Category Filter Chips
// ============================================================================

/**
 * Wire up the POI category filter chip buttons.
 * The amenity toggles + "Find Nearest" now live in a glass panel launched from
 * the radial wheel's "Amenities" item (showPoiFilterPanel), instead of an
 * always-visible strip.
 */

// The purpose shown when the app first runs. The points service returns ~128
// amenity types (~20k features); switching them all on at once would bury the
// map and make clustering work far harder than it needs to. "Getting around"
// is the one people open a map for — fuel, transport, parking — and taking it
// from the panel's own definitions means the default and the card can never
// drift apart. Everything else is downloaded and available offline, just not
// drawn until the user asks for it.
const POI_DEFAULT_CATEGORY = 'transport';

// Fallback if that purpose ever holds nothing (e.g. the upstream vocabulary
// changes), so the map is never blank on first run.
const POI_FALLBACK_AMENITIES = ['fuel', 'bus_station', 'taxi'];

// Module-level filter state so the wheel panel can read/write it.
let poiActiveAmenities = new Set(POI_FALLBACK_AMENITIES);

/**
 * Every amenity present in the loaded data, most common first.
 * @returns {Array<{key:string, emoji:string|null, label:string, count:number}>}
 */
function poiAmenityList() {
  if (!mapView) return [];
  const counts = mapView.getPoiCounts ? mapView.getPoiCounts() : {};
  return Object.entries(counts)
    .map(([key, count]) => ({
      key,
      count,
      emoji: mapView.amenityEmojis?.[key] || null,
      label: key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()),
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

/** Apply the current selection to the map. */
function applyPoiFilter() {
  mapView?.setPoiFilter(new Set(poiActiveAmenities));
}

/** Initialise the amenity filter after POI load (applies the default purpose). */
function initPoiFilter() {
  if (!mapView) return;
  const all = poiAmenityList();
  const present = new Set(all.map((a) => a.key));

  // Ask the panel which kinds the default purpose covers, so this stays in
  // step with the card the user sees.
  const cat = buildCategories(all, new Set()).find((c) => c.key === POI_DEFAULT_CATEGORY);
  const keys = cat?.items.map((i) => i.key) ?? [];

  poiActiveAmenities = new Set(
    keys.length ? keys : POI_FALLBACK_AMENITIES.filter((a) => present.has(a)),
  );
  applyPoiFilter();
  console.log(
    `[POI Filter] ${present.size} amenity types available; showing` +
    ` ${poiActiveAmenities.size} (${cat ? cat.title : 'fallback'})`,
  );
}

/** Open the amenity panel (purpose cards, with the full list behind them). */
function showPoiFilterPanel() {
  if (!mapView) return;
  openAmenityPanel({
    amenities: poiAmenityList(),
    active: poiActiveAmenities,
    onChange: (next) => { poiActiveAmenities = next; applyPoiFilter(); },
  });
}

// ============================================================================
// Weather Alerts (Ghana Meteorological Agency, via cached CAP proxy)
// ============================================================================

// Severity → colour, mirrors MapView.ALERT_COLORS for the pill + panel badges.
const ALERT_SEVERITY = {
  Extreme:  { badge: '#991b1b', order: 4 },
  Severe:   { badge: '#dc2626', order: 3 },
  Moderate: { badge: '#ea580c', order: 2 },
  Minor:    { badge: '#ca8a04', order: 1 },
  Unknown:  { badge: '#6b7280', order: 0 },
};
function alertBadgeColor(sev) { return (ALERT_SEVERITY[sev] || ALERT_SEVERITY.Unknown).badge; }

// On localhost the live feed is usually all-expired, so pull the expired ones
// too — purely so the feature is visible during development. Production
// (import.meta.env.DEV === false) always uses the strict, live-only feed.
const ALERTS_INCLUDE_EXPIRED = import.meta.env.DEV;
// Checking is cheap: the proxy only re-reads GMet every few minutes, and only
// re-downloads the CAP files when the feed's own timestamp has moved.
const ALERTS_POLL_MS = 5 * 60 * 1000;   // check every 5 minutes

let _alertsTimer = null;

/** Kick off the first alert fetch and the periodic refresh. */
function initWeatherAlerts() {
  refreshWeatherAlerts();
  // Wire the pill once (delegated open).
  const pill = document.getElementById('weather-alert-pill');
  if (pill && !pill._wired) {
    pill._wired = true;
    pill.addEventListener('click', () => showWeatherAlertsPanel());
  }
  if (_alertsTimer) clearInterval(_alertsTimer);
  _alertsTimer = setInterval(refreshWeatherAlerts, ALERTS_POLL_MS);
  // Refresh when the app regains focus / connectivity.
  window.addEventListener('online', refreshWeatherAlerts);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refreshWeatherAlerts();
  });
}

/** Fetch alerts, draw polygons, and update the pill. */
async function refreshWeatherAlerts() {
  const res = await fetchAlerts({ includeExpired: ALERTS_INCLUDE_EXPIRED });
  const alerts = res.alerts || [];
  try { mapView?.setWeatherAlerts(alerts); } catch (e) { console.warn('[Alerts] draw failed', e); }
  _updateAlertPill(alerts);
}

/** Show/hide + label the top-centre alert pill. */
function _updateAlertPill(alerts) {
  const pill = document.getElementById('weather-alert-pill');
  if (!pill) return;
  const n = alerts.length;
  if (n === 0) {
    pill.hidden = true;
    pill.classList.remove('visible', 'severe');
    return;
  }
  const top = alerts[0];   // already sorted most-severe first by the proxy
  const label = pill.querySelector('.weather-alert-pill-text');
  if (label) {
    label.textContent = n === 1
      ? (top.event || top.headline || 'Weather alert')
      : `${n} weather alerts`;
  }
  pill.style.background = alertBadgeColor(top.severity);
  pill.classList.toggle('severe', (ALERT_SEVERITY[top.severity]?.order || 0) >= 3);
  pill.hidden = false;
  requestAnimationFrame(() => pill.classList.add('visible'));
}

// ── Alert services registry ────────────────────────────────────────────────
// The "Alerts" wheel item opens a chooser of alert services. Weather (GMet) is
// live now; further providers (ECG power outages, etc.) slot in here — give
// each an entry and set available:true + an open() when its backend is ready.
const ALERT_SERVICES = [
  {
    id: 'weather',
    name: 'Weather Alerts',
    provider: 'Ghana Meteorological Agency',
    icon: 'bi-cloud-lightning-rain',
    color: '#ea580c',
    available: true,
    // Live one-line status shown on the card.
    status: () => {
      const n = getAlertCount();
      return n > 0 ? { text: `${n} active`, tone: 'active' } : { text: 'No active alerts', tone: 'quiet' };
    },
    open: () => showWeatherAlertsPanel(),
  },
  {
    id: 'ecg',
    name: 'Power Outages',
    provider: 'Electricity Company of Ghana',
    icon: 'bi-lightning-charge-fill',
    color: '#2563eb',
    available: false,
    status: () => ({ text: 'Coming soon', tone: 'soon' }),
    open: null,
  },
];

/**
 * Glass chooser of alert services (same styling as the Amenities panel).
 * Currently Weather is the only live service; others show "Coming soon".
 */
function showAlertServicesPanel() {
  injectGlassStyles();
  _ensureAlertPanelStyles();

  const cardsHtml = ALERT_SERVICES.map((svc) => {
    const st = svc.status ? svc.status() : { text: '', tone: 'quiet' };
    const dis = svc.available ? '' : ' service-card-disabled';
    return `
      <button type="button" class="service-card${dis}" data-service="${escapeHtml(svc.id)}"
              ${svc.available ? '' : 'disabled aria-disabled="true"'}>
        <span class="service-icon" style="background:${svc.color};">
          <i class="bi ${escapeHtml(svc.icon)}"></i>
        </span>
        <span class="service-text">
          <span class="service-name">${escapeHtml(svc.name)}</span>
          <span class="service-provider">${escapeHtml(svc.provider)}</span>
        </span>
        <span class="service-status service-status-${st.tone}">${escapeHtml(st.text)}</span>
        ${svc.available ? '<i class="bi bi-chevron-right service-chevron"></i>' : ''}
      </button>`;
  }).join('');

  const overlay = document.createElement('div');
  overlay.className = 'glass-overlay';
  overlay.innerHTML = `
    <div class="glass-card" role="dialog" aria-modal="true">
      <div class="glass-grabber" aria-hidden="true"></div>
      <header class="glass-header">
        <div class="glass-icon" aria-hidden="true"><i class="bi bi-exclamation-triangle"></i></div>
        <div>
          <h2 class="glass-title">Alerts</h2>
          <p class="glass-subtitle">Choose an alert service</p>
        </div>
      </header>
      <div class="glass-body">
        <div class="service-list">${cardsHtml}</div>
      </div>
      <footer class="glass-footer">
        <button type="button" class="glass-btn glass-btn-ghost" data-close>Close</button>
      </footer>
    </div>`;
  document.body.appendChild(overlay);
  requestAnimationFrame(() => overlay.classList.add('glass-open'));

  const close = () => {
    overlay.classList.remove('glass-open');
    setTimeout(() => overlay.remove(), 350);
  };

  overlay.addEventListener('click', (e) => {
    const card = e.target.closest('.service-card');
    if (card && !card.classList.contains('service-card-disabled')) {
      const svc = ALERT_SERVICES.find((s) => s.id === card.dataset.service);
      close();
      // Let the chooser finish closing before the service panel opens.
      if (svc?.open) setTimeout(() => svc.open(), 200);
      return;
    }
    if (e.target.closest('[data-close]') || e.target === overlay) close();
  });
}

/**
 * Glass panel listing the active weather alerts, most severe first.
 * Each row shows event, severity badge, affected areas, timing and the full
 * text; a "Show on map" action zooms to the alert's polygons.
 */
function showWeatherAlertsPanel(opts = {}) {
  injectGlassStyles();
  _ensureAlertPanelStyles();
  const alerts = getCachedAlerts();
  const focusId = opts.focusId || null;

  const fmt = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d) ? '' : d.toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const rowsHtml = alerts.length ? alerts.map((a, i) => {
    const col = alertBadgeColor(a.severity);
    const areas = (a.areas || []).map((ar) => ar.desc).filter(Boolean);
    const areaText = areas.length ? areas.join(', ') : '';
    const validity = [a.onset || a.effective, a.expires].filter(Boolean);
    const when = validity.length === 2 ? `${fmt(validity[0])} → ${fmt(validity[1])}` : fmt(a.expires);
    const expiredTag = a.expired ? `<span class="alert-expired">expired</span>` : '';
    const isFocus = focusId && a.id === focusId;
    return `
      <div class="alert-row${isFocus ? ' focus' : ''}" data-alert-index="${i}" data-alert-id="${escapeHtml(a.id || '')}">
        <div class="alert-row-head">
          <span class="alert-sev" style="background:${col};">${escapeHtml(a.severity || 'Unknown')}</span>
          <span class="alert-event">${escapeHtml(a.event || 'Weather alert')}</span>
          ${expiredTag}
        </div>
        ${a.headline ? `<div class="alert-headline">${escapeHtml(a.headline)}</div>` : ''}
        ${areaText ? `<div class="alert-areas"><i class="bi bi-geo-alt"></i> ${escapeHtml(areaText)}</div>` : ''}
        ${when ? `<div class="alert-when"><i class="bi bi-clock"></i> ${escapeHtml(when)}</div>` : ''}
        ${a.description ? `<div class="alert-desc">${escapeHtml(a.description)}</div>` : ''}
        ${a.instruction ? `<div class="alert-instruction"><strong>What to do:</strong> ${escapeHtml(a.instruction)}</div>` : ''}
        <button type="button" class="alert-showmap" data-showmap="${i}">
          <i class="bi bi-map"></i> Show on map
        </button>
      </div>`;
  }).join('') : `
    <div class="alert-empty">
      <i class="bi bi-check-circle" style="font-size:2rem;opacity:0.4;"></i>
      <p style="margin-top:8px;">No active weather alerts for Ghana right now.</p>
    </div>`;

  const overlay = document.createElement('div');
  overlay.className = 'glass-overlay';
  overlay.innerHTML = `
    <div class="glass-card" role="dialog" aria-modal="true">
      <div class="glass-grabber" aria-hidden="true"></div>
      <header class="glass-header">
        <div class="glass-icon" aria-hidden="true"><i class="bi bi-cloud-lightning-rain"></i></div>
        <div>
          <h2 class="glass-title">Weather Alerts</h2>
          <p class="glass-subtitle">Ghana Meteorological Agency</p>
        </div>
      </header>
      <div class="glass-body">
        <div class="alert-list">${rowsHtml}</div>
        <p class="alert-attribution">Source: Ghana Meteorological Agency (meteo.gov.gh)</p>
      </div>
      <footer class="glass-footer">
        <button type="button" class="glass-btn glass-btn-ghost" data-close>Close</button>
      </footer>
    </div>`;
  document.body.appendChild(overlay);
  requestAnimationFrame(() => overlay.classList.add('glass-open'));

  // If opened by clicking a polygon, bring that alert into view.
  if (focusId) {
    setTimeout(() => {
      const row = overlay.querySelector(`.alert-row[data-alert-id="${CSS.escape(focusId)}"]`);
      row?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
  }

  const close = () => {
    overlay.classList.remove('glass-open');
    setTimeout(() => overlay.remove(), 350);
  };

  overlay.addEventListener('click', (e) => {
    const showBtn = e.target.closest('[data-showmap]');
    if (showBtn) {
      const idx = Number(showBtn.dataset.showmap);
      const a = alerts[idx];
      // Re-draw just this alert's polygons and fit to them.
      if (a && mapView) {
        mapView.setWeatherAlerts([a]);
        mapView.fitWeatherAlerts();
      }
      close();
      // Restore the full set shortly after (so the map isn't left filtered).
      setTimeout(() => mapView?.setWeatherAlerts(getCachedAlerts()), 800);
      return;
    }
    if (e.target.closest('[data-close]') || e.target === overlay) close();
  });
}

let _alertPanelStyled = false;
function _ensureAlertPanelStyles() {
  if (_alertPanelStyled) return;
  _alertPanelStyled = true;
  const css = `
    .alert-list { display:flex; flex-direction:column; gap:14px; max-height:60vh; overflow-y:auto; }
    .alert-row { padding:12px 14px; border-radius:14px; background:rgba(255,255,255,0.55);
      border:1px solid rgba(0,0,0,0.06); transition:box-shadow 0.3s, border-color 0.3s; }
    .alert-row.focus { border-color:rgba(139,0,139,0.55);
      box-shadow:0 0 0 3px rgba(139,0,139,0.18); }
    .alert-row-head { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
    .alert-sev { color:#fff; font-size:0.72rem; font-weight:800; letter-spacing:0.03em;
      padding:2px 9px; border-radius:999px; text-transform:uppercase; }
    .alert-event { font-weight:700; color:#1c1020; }
    .alert-expired { font-size:0.68rem; font-weight:700; color:#6b7280;
      background:rgba(107,114,128,0.15); padding:2px 8px; border-radius:999px; }
    .alert-headline { margin-top:6px; font-weight:600; color:#2a1830; font-size:0.92rem; }
    .alert-areas, .alert-when { margin-top:5px; font-size:0.82rem; color:rgba(40,20,45,0.7); }
    .alert-desc { margin-top:8px; font-size:0.86rem; line-height:1.45; color:#33263a; }
    .alert-instruction { margin-top:8px; font-size:0.84rem; line-height:1.45; color:#33263a;
      background:rgba(255,233,106,0.28); padding:8px 10px; border-radius:10px; }
    .alert-showmap { margin-top:10px; padding:7px 14px; border-radius:999px; font-size:0.82rem;
      font-weight:700; border:1px solid rgba(139,0,139,0.35); background:rgba(255,255,255,0.6);
      color:#8B008B; cursor:pointer; -webkit-tap-highlight-color:transparent; }
    .alert-showmap:active { background:rgba(139,0,139,0.12); }
    .alert-empty { text-align:center; padding:28px 10px; color:rgba(40,20,45,0.7); }
    .alert-attribution { margin-top:14px; font-size:0.72rem; color:rgba(40,20,45,0.5); text-align:center; }

    /* Alert-services chooser cards */
    .service-list { display:flex; flex-direction:column; gap:10px; }
    .service-card { display:flex; align-items:center; gap:12px; width:100%; text-align:left;
      padding:12px 14px; border-radius:14px; background:rgba(255,255,255,0.55);
      border:1px solid rgba(0,0,0,0.06); cursor:pointer; -webkit-tap-highlight-color:transparent;
      transition:background 0.14s, transform 0.1s, box-shadow 0.14s; }
    .service-card:not(.service-card-disabled):hover { background:rgba(255,255,255,0.8);
      box-shadow:0 3px 12px rgba(0,0,0,0.1); }
    .service-card:not(.service-card-disabled):active { transform:scale(0.99); }
    .service-card-disabled { opacity:0.55; cursor:default; }
    .service-icon { flex:0 0 auto; width:42px; height:42px; border-radius:12px; color:#fff;
      display:flex; align-items:center; justify-content:center; font-size:20px; }
    .service-text { display:flex; flex-direction:column; min-width:0; flex:1 1 auto; }
    .service-name { font-weight:700; color:#1c1020; font-size:0.98rem; }
    .service-provider { font-size:0.78rem; color:rgba(40,20,45,0.6); overflow:hidden;
      text-overflow:ellipsis; white-space:nowrap; }
    .service-status { flex:0 0 auto; font-size:0.74rem; font-weight:700; padding:3px 9px;
      border-radius:999px; white-space:nowrap; }
    .service-status-active { background:rgba(234,88,12,0.15); color:#c2410c; }
    .service-status-quiet  { background:rgba(34,139,34,0.14); color:#2f6b2f; }
    .service-status-soon   { background:rgba(107,114,128,0.15); color:#6b7280; }
    .service-chevron { flex:0 0 auto; color:rgba(40,20,45,0.35); font-size:0.9rem; }
  `;
  const el = document.createElement('style');
  el.textContent = css;
  document.head.appendChild(el);
}

// ============================================================================
// Find Nearest POI
// ============================================================================

/**
 * Get the user's geolocation, compute distances to visible POI,
 * and show the sorted results in the bottom sheet.
 */
function findNearestPOI() {
  if (!mapView) return;

  const sheetBody = bottomSheet.body;
  if (!sheetBody) return;

  // Update bottom sheet header
  const sheetLabel = document.getElementById('bottomSheetLabel');
  if (sheetLabel) {
    sheetLabel.innerHTML = '<i class="bi bi-signpost-split me-2"></i>Nearest POI';
  }

  // Show loading state and open sheet
  sheetBody.innerHTML = `
    <div class="nearest-empty">
      <div class="spinner-border spinner-border-sm text-primary mb-2" role="status"></div>
      <div>${escapeHtml(t('general.loading'))}</div>
    </div>
  `;
  bottomSheet.open('half');

  // Get geolocation
  if (!navigator.geolocation) {
    sheetBody.innerHTML = `
      <div class="nearest-empty">
        <i class="bi bi-exclamation-triangle"></i>
        Geolocation is not supported by your browser.
      </div>
    `;
    return;
  }

  navigator.geolocation.getCurrentPosition(
    (position) => {
      const lon = position.coords.longitude;
      const lat = position.coords.latitude;

      console.log('[Nearest] User position:', lon, lat);

      const results = mapView.findNearestPOI(lon, lat, { limit: 20 });

      if (results.length === 0) {
        sheetBody.innerHTML = `
          <div class="nearest-empty">
            <i class="bi bi-geo-alt"></i>
            No POI found. Try enabling more categories.
          </div>
        `;
        return;
      }

      // Render results list
      sheetBody.innerHTML = `
        <div style="font-size: 12px; color: var(--bw-text-muted); margin-bottom: 10px;">
          ${results.length} results from your location
        </div>
        <ul class="nearest-results">
          ${results.map((r, i) => {
            const distStr = r.distance < 1000
              ? `${Math.round(r.distance)} m`
              : `${(r.distance / 1000).toFixed(1)} km`;
            const amenityLabel = r.amenity.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            const displayName = r.name || amenityLabel;
            return `
              <li class="nearest-item" data-lon="${r.lon}" data-lat="${r.lat}">
                <span class="nearest-item-emoji">${r.emoji}</span>
                <div class="nearest-item-info">
                  <div class="nearest-item-name">${escapeHtml(displayName)}</div>
                  <div class="nearest-item-detail">${escapeHtml(amenityLabel)}${r.brand ? ' · ' + escapeHtml(r.brand) : ''}</div>
                </div>
                <span class="nearest-item-dist">${distStr}</span>
              </li>
            `;
          }).join('')}
        </ul>
      `;

      // Wire click → zoom to POI
      sheetBody.querySelectorAll('.nearest-item').forEach(item => {
        item.addEventListener('click', () => {
          const pLon = parseFloat(item.dataset.lon);
          const pLat = parseFloat(item.dataset.lat);
          mapView.zoomTo(pLon, pLat, 16);
          bottomSheet.close();
        });
      });
    },
    (error) => {
      console.warn('[Nearest] Geolocation error:', error.message);
      sheetBody.innerHTML = `
        <div class="nearest-empty">
          <i class="bi bi-exclamation-triangle"></i>
          Could not get your location.<br>
          <small>${escapeHtml(error.message)}</small>
        </div>
      `;
    },
    { enableHighAccuracy: true, timeout: 10000 }
  );
}

// ============================================================================
// OSRM Routing / "Navigate to" POI
// ============================================================================

/**
 * Get the user's geolocation, request a route from OSRM, and display it.
 * @param {{lon:number, lat:number, name:string, amenity:string, emoji:string}} poi
 */
function navigateToOSRM(poi) {
  if (!mapView) return;

  const routeInfo = document.getElementById('route-info');

  // Show loading state
  if (routeInfo) {
    routeInfo.innerHTML = `
      <div class="route-info-content">
        <div class="spinner-border spinner-border-sm me-2" role="status"></div>
        <span>Finding route to ${escapeHtml(poi.emoji + ' ' + (poi.name || 'POI'))}…</span>
      </div>
    `;
    routeInfo.classList.add('visible');
  }

  if (!navigator.geolocation) {
    showError('Geolocation is not supported by your browser.');
    if (routeInfo) routeInfo.classList.remove('visible');
    return;
  }

  navigator.geolocation.getCurrentPosition(
    async (position) => {
      const fromLon = position.coords.longitude;
      const fromLat = position.coords.latitude;

      try {
        const url = `https://router.project-osrm.org/route/v1/driving/${fromLon},${fromLat};${poi.lon},${poi.lat}?overview=full&geometries=geojson`;
        console.log('[OSRM] Requesting route:', url);

        const response = await fetch(url);
        const data = await response.json();

        if (data.code !== 'Ok' || !data.routes?.length) {
          throw new Error(data.message || 'No route found');
        }

        const route = data.routes[0];
        const coords = route.geometry.coordinates;
        const distance = route.legs[0].distance;  // metres
        const duration = route.legs[0].duration;  // seconds

        console.log('[OSRM] Route found:', (distance / 1000).toFixed(1), 'km,', Math.ceil(duration / 60), 'min');

        // Draw the route line on the map
        mapView.showRoute(coords);

        // Show route info bar
        showRouteInfo(poi, distance, duration);

      } catch (error) {
        console.error('[OSRM] Routing failed:', error);
        showError('Could not find route: ' + error.message);
        if (routeInfo) routeInfo.classList.remove('visible');
      }
    },
    (error) => {
      console.warn('[OSRM] Geolocation error:', error.message);
      showError('Could not get your location: ' + error.message);
      if (routeInfo) routeInfo.classList.remove('visible');
    },
    { enableHighAccuracy: true, timeout: 10000 }
  );
}

/**
 * Show the route info bar above the dock with distance, duration, and clear button.
 */
function showRouteInfo(poi, distance, duration) {
  const routeInfo = document.getElementById('route-info');
  if (!routeInfo) return;

  const distStr = distance < 1000
    ? `${Math.round(distance)} m`
    : `${(distance / 1000).toFixed(1)} km`;

  const durMin = Math.ceil(duration / 60);
  const durStr = durMin < 60
    ? `${durMin} min`
    : `${Math.floor(durMin / 60)}h ${durMin % 60}m`;

  routeInfo.innerHTML = `
    <div class="route-info-content">
      <span class="route-info-dest">${poi.emoji} ${escapeHtml(poi.name || 'POI')}</span>
      <span class="route-info-stats">
        <i class="bi bi-signpost-2"></i> ${distStr}
        <span class="route-info-dot">·</span>
        <i class="bi bi-clock"></i> ${durStr}
      </span>
    </div>
    <button class="route-info-close" type="button" title="Clear route">
      <i class="bi bi-x-lg"></i>
    </button>
  `;
  routeInfo.classList.add('visible');

  routeInfo.querySelector('.route-info-close').addEventListener('click', () => {
    mapView.clearRoute();
    routeInfo.classList.remove('visible');
  });
}

// ============================================================================
// Fieldwork Mode (High Contrast)
// ============================================================================

const FIELDWORK_STORAGE_KEY = 'bisawofa-fieldwork';

/**
 * Wire the fieldwork mode toggle switch in the settings sheet.
 * Reads/writes localStorage and toggles `body.fieldwork-mode`.
 */
function wireFieldworkToggle() {
  const toggle = document.getElementById('fieldwork-toggle');
  if (!toggle) return;

  // Restore saved state
  const saved = localStorage.getItem(FIELDWORK_STORAGE_KEY) === '1';
  toggle.checked = saved;
  applyFieldworkMode(saved);

  toggle.addEventListener('change', () => {
    const enabled = toggle.checked;
    localStorage.setItem(FIELDWORK_STORAGE_KEY, enabled ? '1' : '0');
    applyFieldworkMode(enabled);
  });
}

/**
 * Apply handedness: mirror the radial wheel and, so they don't overlap,
 * swap the Settings cog to the opposite edge (body.bw-left-handed drives the
 * cog via CSS). The wheel (frequent actions) takes the reachable side; the
 * secondary Settings cog moves to the vacated edge.
 */
function applyHandedness(left) {
  document.body.classList.toggle('bw-left-handed', !!left);
  radialMenu?.setSide(left ? 'left' : 'right');
  // The layer dial reads the body class, so it is refreshed after the toggle.
  radialDial?.refreshSide();
}

/**
 * Wire the left-handed toggle: mirrors the radial wheel to the left edge.
 */
function wireLeftHandedToggle() {
  const toggle = document.getElementById('left-handed-toggle');
  if (!toggle) return;

  toggle.checked = isLeftHanded();
  applyHandedness(toggle.checked);   // reflect saved state (e.g. after re-render)

  toggle.addEventListener('change', () => {
    const left = toggle.checked;
    localStorage.setItem(LEFT_HANDED_STORAGE_KEY, left ? '1' : '0');
    applyHandedness(left);
  });
}

/**
 * Apply or remove fieldwork mode on the body and update theme-color.
 */
function applyFieldworkMode(enabled) {
  document.body.classList.toggle('fieldwork-mode', enabled);

  // Update the theme-color meta tag so the browser chrome matches
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.content = enabled ? '#000000' : '#8B008B';
  }
}

// ============================================================================
// Font Size Control
// ============================================================================

const FONTSIZE_STORAGE_KEY = 'bisawofa-fontsize';
const FONTSIZE_LABELS = ['S', 'M', 'L', 'XL'];
const FONTSIZE_DEFAULT = 1;   // index 1 = 16px (browser default)

/**
 * Wire the A / A+ font-size stepper buttons in the settings sheet.
 */
function wireFontSizeControl() {
  const downBtn = document.getElementById('font-size-down');
  const upBtn   = document.getElementById('font-size-up');
  const label   = document.getElementById('font-size-label');
  if (!downBtn || !upBtn || !label) return;

  let step = getFontSizeStep();
  updateFontSizeUI(step, downBtn, upBtn, label);

  downBtn.addEventListener('click', () => {
    if (step <= 0) return;
    step--;
    applyFontSize(step);
    updateFontSizeUI(step, downBtn, upBtn, label);
  });

  upBtn.addEventListener('click', () => {
    if (step >= FONTSIZE_LABELS.length - 1) return;
    step++;
    applyFontSize(step);
    updateFontSizeUI(step, downBtn, upBtn, label);
  });
}

/**
 * Read the saved font-size step from localStorage.
 */
function getFontSizeStep() {
  const saved = parseInt(localStorage.getItem(FONTSIZE_STORAGE_KEY), 10);
  return isNaN(saved) ? FONTSIZE_DEFAULT : Math.max(0, Math.min(saved, FONTSIZE_LABELS.length - 1));
}

/**
 * Apply a font-size step by setting a class on <html>.
 * Step 1 is the default (16px) → no class needed.
 */
function applyFontSize(step) {
  const html = document.documentElement;
  // Remove all font-size-* classes
  html.classList.remove('font-size-0', 'font-size-2', 'font-size-3');
  // Add the class for this step (skip step 1 = default)
  if (step !== 1) {
    html.classList.add(`font-size-${step}`);
  }
  localStorage.setItem(FONTSIZE_STORAGE_KEY, String(step));
}

/**
 * Update the stepper UI (label text, disabled states).
 */
function updateFontSizeUI(step, downBtn, upBtn, label) {
  label.textContent = FONTSIZE_LABELS[step];
  downBtn.disabled = step <= 0;
  upBtn.disabled = step >= FONTSIZE_LABELS.length - 1;
}

// ============================================================================
// Voice Input
//
// Speech-to-text for any `.voice-mic-btn[data-voice-target]`. The engine lives
// in src/voice.js and picks the right backend automatically: the native
// Capacitor speech plugin on iOS/Android (works where the web Speech API does
// not), or window.SpeechRecognition in the browser.
// ============================================================================
// initVoiceInput is imported from ./src/voice.js (see imports at top of file).

/**
 * Restore the settings content inside the bottom sheet body.
 * Called when the Settings dock button is tapped after Nearest POI
 * (or other content) replaced the sheet body.
 */
function restoreSettingsSheet() {
  const sheetBody = document.getElementById('bottom-sheet-body');
  if (!sheetBody) return;

  // If settings content is already showing, nothing to do
  if (sheetBody.querySelector('#settings-content')) return;

  // Restore header
  const sheetLabel = document.getElementById('bottomSheetLabel');
  if (sheetLabel) {
    sheetLabel.innerHTML = `<i class="bi bi-sliders me-2"></i><span data-i18n="nav.bottomPanel">${escapeHtml(t('dock.settings'))}</span>`;
  }

  // Restore settings HTML
  const isChecked = localStorage.getItem(FIELDWORK_STORAGE_KEY) === '1';
  const fontStep = getFontSizeStep();
  sheetBody.innerHTML = `
    <div id="settings-content">
      <div class="settings-item">
        <div class="settings-item-info">
          <span class="settings-item-icon">☀️</span>
          <div>
            <div class="settings-item-label" data-i18n="settings.fieldwork">${escapeHtml(t('settings.fieldwork'))}</div>
            <div class="settings-item-desc" data-i18n="settings.fieldworkDesc">${escapeHtml(t('settings.fieldworkDesc'))}</div>
          </div>
        </div>
        <div class="form-check form-switch">
          <input class="form-check-input" type="checkbox" role="switch" id="fieldwork-toggle" ${isChecked ? 'checked' : ''}>
        </div>
      </div>
      <div class="settings-item">
        <div class="settings-item-info">
          <span class="settings-item-icon">🤚</span>
          <div>
            <div class="settings-item-label" data-i18n="settings.leftHanded">${escapeHtml(t('settings.leftHanded'))}</div>
            <div class="settings-item-desc" data-i18n="settings.leftHandedDesc">${escapeHtml(t('settings.leftHandedDesc'))}</div>
          </div>
        </div>
        <div class="form-check form-switch">
          <input class="form-check-input" type="checkbox" role="switch" id="left-handed-toggle" ${isLeftHanded() ? 'checked' : ''}>
        </div>
      </div>
      <div class="settings-item">
        <div class="settings-item-info">
          <span class="settings-item-icon">🔤</span>
          <div>
            <div class="settings-item-label" data-i18n="settings.fontSize">${escapeHtml(t('settings.fontSize'))}</div>
            <div class="settings-item-desc" data-i18n="settings.fontSizeDesc">${escapeHtml(t('settings.fontSizeDesc'))}</div>
          </div>
        </div>
        <div class="font-size-stepper">
          <button type="button" id="font-size-down" aria-label="Decrease font size">A</button>
          <span class="font-size-step" id="font-size-label">${FONTSIZE_LABELS[fontStep]}</span>
          <button type="button" id="font-size-up" aria-label="Increase font size">A</button>
        </div>
      </div>
    </div>
  `;

  // Re-wire controls since we recreated the DOM elements
  wireFieldworkToggle();
  wireLeftHandedToggle();
  wireFontSizeControl();
}

// ============================================================================
// Circle ↔ POI intersection counting
// ============================================================================

/**
 * Count fuel, bus_station, and taxi POI features inside a circle geometry.
 *
 * Uses extent pre-filter + precise intersectsCoordinate for efficiency.
 *
 * @param {import('ol/geom/Circle').default} circleGeom  Circle in EPSG:3857
 * @param {import('ol/source/Vector').default} poiSource  POI VectorSource
 * @returns {{ fuel: number, bus_station: number, taxi: number }}
 */
function countPOIsInCircle(circleGeom, poiSource) {
  const targets = ['fuel', 'bus_station', 'taxi'];
  const counts = { fuel: 0, bus_station: 0, taxi: 0 };
  const extent = circleGeom.getExtent();

  poiSource.forEachFeatureInExtent(extent, (feature) => {
    const amenity = feature.get('amenity');
    if (!targets.includes(amenity)) return;

    const coord = feature.getGeometry().getCoordinates();
    if (circleGeom.intersectsCoordinate(coord)) {
      counts[amenity]++;
    }
  });

  return counts;
}

// ============================================================================
// Map tap ripple
// ============================================================================

/**
 * Show a brief ripple animation at the given pixel position on the map.
 */
function showMapTapRipple(pixel) {
  const mapEl = document.getElementById('map');
  if (!mapEl) return;

  const ripple = document.createElement('div');
  ripple.className = 'map-tap-ripple';
  ripple.style.left = pixel[0] + 'px';
  ripple.style.top = pixel[1] + 'px';
  mapEl.appendChild(ripple);

  // Trigger animation on next frame so the browser registers the initial state
  requestAnimationFrame(() => ripple.classList.add('animate'));

  // Remove after animation completes
  ripple.addEventListener('animationend', () => ripple.remove());
}

// ============================================================================
// Sync (placeholder - implement based on your backend)
// ============================================================================

async function syncData() {
  if (!isOnline()) {
    console.log('[App] Cannot sync - offline');
    return;
  }

  // TODO: Implement sync with your backend
  console.log('[App] Sync placeholder - implement based on your backend');
}

// ============================================================================
// Utilities
// ============================================================================

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function showError(message) {
  const el = document.getElementById('error-message');
  if (el) {
    el.querySelector('.message-text').textContent = message;
    el.classList.remove('d-none');

    setTimeout(() => {
      el.classList.add('d-none');
    }, 5000);
  } else {
    console.error(message);
  }
}

function showSuccess(message) {
  const el = document.getElementById('success-message');
  if (el) {
    el.querySelector('.message-text').textContent = message;
    el.classList.remove('d-none');

    setTimeout(() => {
      el.classList.add('d-none');
    }, 3000);
  } else {
    console.log(message);
  }
}

// ============================================================================
// Start Application
// ============================================================================

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  initApp();
}
